/* Les modèles de document : un par usine, plus celui du devis client.
   Chaque usine reçoit le format qu'elle attend — sa langue, ses intitulés. */

import { useMemo, useState } from "react";
import { useClients, useModeles, useUsines, useVentes } from "@/lib/donnees";
import { autorise, useNiveau } from "@/lib/session";
import { supabase } from "@/lib/supabase";
import { Empty, Section, Segment } from "@/ui/primitives";
import { Champ } from "@/ui/Drawer";
import { Document } from "@/ui/Document";
import { useToast } from "@/ui/Toast";
import { COLONNES_DOC, LANGUES, colonnesDe, colonnesParDefaut } from "@/domain/documents";
import type { ColonneDoc } from "@/domain/documents";
import type { LigneVente, ModeleDocument } from "@/types/metier";

/* De quoi montrer l'aperçu quand l'usine n'a encore aucune vente. */
const LIGNES_EXEMPLE: LigneVente[] = [
  { id: "x1", vente_id: "x", produit_id: null, ref: "REF-001", nom: "Première référence",
    gamme: null, conditionnement: "5 kg", prix: 1.32, qte: 1000, cartons: null, par_carton: null, total: 1320, position: 0 },
  { id: "x2", vente_id: "x", produit_id: null, ref: "REF-002", nom: "Deuxième référence",
    gamme: null, conditionnement: "1 kg", prix: 2.48, qte: 400, cartons: null, par_carton: null, total: 992, position: 1 },
];

export function Modeles() {
  const toast = useToast();
  const modifiable = autorise(useNiveau(), "agent");
  const { donnees: modeles, chargement, erreur, recharger } = useModeles();
  const { donnees: usines } = useUsines();
  const { donnees: ventes } = useVentes();
  const { donnees: clients } = useClients();
  const [cible, setCible] = useState<string>("client");
  const [occupe, setOccupe] = useState(false);

  const usine = usines.find((u) => u.id === cible) ?? null;
  const modele = useMemo<ModeleDocument | null>(() => {
    if (cible === "client") return modeles.find((m) => m.cible === "client") ?? null;
    return modeles.find((m) => m.cible === "usine" && m.usine_id === cible) ?? null;
  }, [modeles, cible]);

  /* Une vraie vente de cette usine vaut mieux qu'un exemple. */
  const exemple = useMemo(() => {
    const v = ventes.find(
      (x) => (cible === "client" || x.usine_id === cible) && (x.lignes?.length ?? 0) > 0,
    );
    return v
      ? { vente: v, lignes: [...(v.lignes ?? [])].sort((a, b) => a.position - b.position) }
      : {
          vente: { reference: "SO-0000", date_document: new Date().toISOString().slice(0, 10) },
          lignes: LIGNES_EXEMPLE,
        };
  }, [ventes, cible]);

  const destinataire =
    cible === "client"
      ? (clients.find((c) => c.id === (exemple.vente as { client_id?: string }).client_id)?.nom ??
        "Client destinataire")
      : (usine?.nom ?? "Usine destinataire");

  async function agir(quoi: () => Promise<void>, message: string) {
    if (occupe) return;
    setOccupe(true);
    try {
      await quoi();
      recharger();
      toast(message);
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'enregistrement a échoué.");
    } finally {
      setOccupe(false);
    }
  }

  const ecrire = (patch: Record<string, unknown>, message: string) =>
    void agir(async () => {
      if (!modele) return;
      const { error } = await supabase.from("modeles_document").update(patch).eq("id", modele.id);
      if (error) throw new Error(error.message);
    }, message);

  const creer = () =>
    void agir(async () => {
      const { error } = await supabase.from("modeles_document").insert({
        usine_id: cible === "client" ? null : cible,
        cible: cible === "client" ? "client" : "usine",
        titre: cible === "client" ? "DEVIS" : "BON DE COMMANDE FOURNISSEUR",
        langue: "fr",
        incoterm: cible === "client" ? "EXW" : null,
        unite: "Colis",
        colonnes: colonnesParDefaut(cible === "client"),
        mentions:
          cible === "client"
            ? "Offre valable 30 jours. Prix hors taxes, hors transport."
            : "Merci de confirmer la disponibilité sous 48 h.",
      });
      if (error) throw new Error(error.message);
    }, "Modèle créé.");

  const majColonne = (cle: string, patch: Partial<ColonneDoc>) => {
    if (!modele) return;
    const suivantes = colonnesDe(modele).map((c) => (c.cle === cle ? { ...c, ...patch } : c));
    ecrire({ colonnes: suivantes }, "Colonnes enregistrées.");
  };

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  const cols = modele ? colonnesDe(modele) : [];

  return (
    <>
      <div className="filterbar">
        <Segment
          label="Destinataire"
          valeur={cible}
          onChange={setCible}
          options={[
            { cle: "client", nom: "Devis client" },
            ...usines.map((u) => ({ cle: u.id, nom: u.nom })),
          ]}
        />
      </div>

      <div className="pad">
        {!modele ? (
          <Section titre="Aucun modèle" note={chargement ? "chargement…" : "à créer"}>
            <div className="card card-pad">
              <Empty
                titre="Ce destinataire n'a pas encore de modèle"
                detail="Créez-en un : il part du format français, toutes colonnes visibles."
              />
              {modifiable ? (
                <div className="fiche-actions">
                  <button type="button" className="btn btn-primary" disabled={occupe} onClick={creer}>
                    Créer le modèle
                  </button>
                </div>
              ) : null}
            </div>
          </Section>
        ) : (
          <div className="grid2">
            <div>
              <Section titre="Réglages du modèle">
                <div className="card card-pad">
                  <Champ id="m-titre" label="Titre du document">
                    <input
                      id="m-titre"
                      type="text"
                      defaultValue={modele.titre}
                      disabled={!modifiable || occupe}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (!v || v === modele.titre) return;
                        ecrire({ titre: v }, "Titre enregistré.");
                      }}
                    />
                  </Champ>
                  <div className="champ-duo">
                    <Champ id="m-langue" label="Langue">
                      <select
                        id="m-langue"
                        defaultValue={modele.langue}
                        disabled={!modifiable || occupe}
                        onChange={(e) => ecrire({ langue: e.target.value }, "Langue enregistrée.")}
                      >
                        {LANGUES.map((l) => (
                          <option key={l.cle} value={l.cle}>
                            {l.nom}
                          </option>
                        ))}
                      </select>
                    </Champ>
                    <Champ id="m-unite" label="Unité affichée">
                      <input
                        id="m-unite"
                        type="text"
                        defaultValue={modele.unite ?? ""}
                        placeholder="Colis"
                        disabled={!modifiable || occupe}
                        onBlur={(e) => {
                          const v = e.target.value.trim() || null;
                          if (v === (modele.unite ?? null)) return;
                          ecrire({ unite: v }, "Unité enregistrée.");
                        }}
                      />
                    </Champ>
                  </div>
                  <Champ id="m-incoterm" label="Incoterm">
                    <input
                      id="m-incoterm"
                      type="text"
                      defaultValue={modele.incoterm ?? ""}
                      placeholder="EXW Vercelli"
                      disabled={!modifiable || occupe}
                      onBlur={(e) => {
                        const v = e.target.value.trim() || null;
                        if (v === (modele.incoterm ?? null)) return;
                        ecrire({ incoterm: v }, "Incoterm enregistré.");
                      }}
                    />
                  </Champ>
                  <Champ id="m-mentions" label="Mentions en pied de page">
                    <textarea
                      id="m-mentions"
                      defaultValue={modele.mentions ?? ""}
                      disabled={!modifiable || occupe}
                      onBlur={(e) => {
                        const v = e.target.value.trim() || null;
                        if (v === (modele.mentions ?? null)) return;
                        ecrire({ mentions: v }, "Mentions enregistrées.");
                      }}
                    />
                  </Champ>
                </div>
              </Section>

              <Section titre="Colonnes" note="intitulé envoyé, et ce qui s'affiche">
                <div className="card card-pad">
                  {cols.map((c) => {
                    const def = COLONNES_DOC.find((x) => x.cle === c.cle);
                    return (
                      <div className="champ-duo" key={c.cle}>
                        <Champ id={`col-${c.cle}`} label={def?.defaut ?? c.cle}>
                          <input
                            id={`col-${c.cle}`}
                            type="text"
                            defaultValue={c.libelle}
                            disabled={!modifiable || occupe}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (!v || v === c.libelle) return;
                              majColonne(c.cle, { libelle: v });
                            }}
                          />
                        </Champ>
                        <Champ id={`vis-${c.cle}`} label="Affichée">
                          <select
                            id={`vis-${c.cle}`}
                            value={c.visible ? "oui" : "non"}
                            disabled={!modifiable || occupe}
                            onChange={(e) => majColonne(c.cle, { visible: e.target.value === "oui" })}
                          >
                            <option value="oui">Oui</option>
                            <option value="non">Non</option>
                          </select>
                        </Champ>
                      </div>
                    );
                  })}
                </div>
              </Section>
            </div>

            <div>
              <Section
                titre="Aperçu"
                note={
                  ventes.some((x) => cible === "client" || x.usine_id === cible)
                    ? "sur une vente réelle"
                    : "sur un exemple"
                }
              >
                <Document
                  vente={exemple.vente}
                  lignes={exemple.lignes}
                  modele={modele}
                  usine={usine}
                  destinataire={destinataire}
                />
              </Section>
              {cible !== "client" ? (
                <div className="vide-ligne">
                  Les prix de vente ne partent jamais à l'usine : laissez les colonnes de montants
                  masquées sur ce modèle.
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
